import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Provider } from 'react-redux';
import { Toaster } from 'react-hot-toast';
import { store } from './store';
import { AuthProvider } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { AIAgentProvider } from './contexts/AIAgentContext';

// Layout
import Layout from './components/Layout';
import PrivateRoute from './components/PrivateRoute';
import ErrorBoundary from './components/ErrorBoundary';

// Pages
import LandingPage from './pages/LandingPage';
import IntroPage from './pages/IntroPage';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Assistants from './pages/Assistants';
import AssistantDetails from './pages/AssistantDetails';
import AssistantBuilderPage from './pages/AssistantBuilderPage';
import AssistantEdit from './pages/AssistantEdit';
import AssistantBuilder from './pages/AssistantBuilder';
import Conversations from './pages/Conversations';
import ConversationView from './pages/ConversationView';
import FlowBuilder from './pages/FlowBuilder';
import KnowledgeBase from './pages/KnowledgeBase';
import TagsPage from './pages/Tags';
import Calendar from './pages/Calendar';
import Billing from './pages/Billing';
import Analytics from './pages/Analytics';
import AnalyticsReport from './pages/AnalyticsReport';
import Settings from './pages/Settings';
import VocalsPerMinute from './pages/VocalsPerMinute';
import SummaryAssistant from './pages/SummaryAssistant';
import CallStatsPage from './pages/CallStatsPage';
import PhoneNumbersPage from './pages/PhoneNumbersPage';
import PhoneNumberDetails from './pages/PhoneNumberDetails';
import LoopletAIModule from './modules/looplet-ai';
import Widgets from './pages/Widgets';
import AIChatDemo from './pages/AIChatDemo';

function App() {
  return (
    <ErrorBoundary>
      <Provider store={store}>
        <ThemeProvider>
          <AuthProvider>
            <SocketProvider>
            <AIAgentProvider>
          <Router 
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true
            }}
          >
            <Toaster 
              position="top-right" 
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
                success: {
                  style: {
                    background: '#10b981',
                  },
                },
                error: {
                  style: {
                    background: '#ef4444',
                  },
                },
              }}
            />
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/intro" element={<IntroPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />

              <Route path="/dashboard" element={<PrivateRoute><Layout /></PrivateRoute>}>
                <Route index element={<Dashboard />} />
              </Route>
              <Route path="/assistants" element={<PrivateRoute><Layout /></PrivateRoute>}>
                <Route index element={<Assistants />} />
                <Route path=":id" element={<AssistantDetails />} />
                <Route path="new" element={<AssistantEdit />} />
                <Route path=":id/builder" element={<AssistantBuilder />} />
              </Route>
              
              {/* Assistant Builder without Layout */}
              <Route path="/assistants/:id/edit" element={<PrivateRoute><AssistantBuilderPage /></PrivateRoute>} />
              
              {/* Call Stats Page */}
              <Route path="/call-stats" element={<PrivateRoute><Layout /></PrivateRoute>}>
                <Route index element={<CallStatsPage />} />
              </Route>
              
              {/* Phone Numbers Page */}
              <Route path="/phone-numbers" element={<PrivateRoute><Layout /></PrivateRoute>}>
                <Route index element={<PhoneNumbersPage />} />
                <Route path=":id" element={<PhoneNumberDetails />} />
              </Route>
              <Route path="/conversations" element={<PrivateRoute><Layout /></PrivateRoute>}>
                <Route index element={<Conversations />} />
                <Route path=":id" element={
                  <ErrorBoundary>
                    <ConversationView />
                  </ErrorBoundary>
                } />
              </Route>
              <Route path="/ai-chat-demo" element={
                <PrivateRoute>
                  <ErrorBoundary>
                    <AIChatDemo />
                  </ErrorBoundary>
                </PrivateRoute>
              } />
              <Route path="/flows" element={<PrivateRoute><Layout /></PrivateRoute>}>
                <Route index element={<FlowBuilder />} />
              </Route>
              <Route path="/flow-builder" element={<PrivateRoute><Layout /></PrivateRoute>}>
                <Route index element={<FlowBuilder />} />
              </Route>
              <Route path="/knowledge" element={<PrivateRoute><Layout /></PrivateRoute>}>
                <Route index element={<KnowledgeBase />} />
              </Route>
              <Route path="/widgets" element={<PrivateRoute><Layout /></PrivateRoute>}>
                <Route index element={<Widgets />} />
              </Route>
              <Route path="/tags" element={<PrivateRoute><Layout /></PrivateRoute>}>
                <Route index element={<TagsPage />} />
              </Route>
              <Route path="/vocals-per-minute" element={<PrivateRoute><Layout /></PrivateRoute>}>
                <Route index element={<VocalsPerMinute />} />
              </Route>
              <Route path="/summary-assistant" element={<PrivateRoute><Layout /></PrivateRoute>}>
                <Route index element={<SummaryAssistant />} />
              </Route>
              <Route path="/calendar" element={<PrivateRoute><Layout /></PrivateRoute>}>
                <Route index element={<Calendar />} />
              </Route>
              <Route path="/billing" element={<PrivateRoute><Layout /></PrivateRoute>}>
                <Route index element={<Billing />} />
              </Route>
              <Route path="/analytics" element={<PrivateRoute><Layout /></PrivateRoute>}>
                <Route index element={<Analytics />} />
              </Route>
              <Route path="/analytics-report" element={<PrivateRoute><Layout /></PrivateRoute>}>
                <Route index element={<AnalyticsReport />} />
              </Route>
              <Route path="/ai/*" element={<PrivateRoute><Layout /></PrivateRoute>}>
                <Route path="*" element={<LoopletAIModule basePath="/ai" />} />
              </Route>
              <Route path="/settings" element={<PrivateRoute><Layout /></PrivateRoute>}>
                <Route index element={<Settings />} />
              </Route>
            </Routes>
          </Router>
            </AIAgentProvider>
            </SocketProvider>
          </AuthProvider>
        </ThemeProvider>
      </Provider>
    </ErrorBoundary>
  );
}

export default App;